import { Controller } from '@hotwired/stimulus';

/**
 * Formats a number input with the active Sede's currency separators.
 *
 *   data-controller="currency-input"
 *   data-currency-input-symbol-value="€"     (visual only — not enforced)
 *   data-currency-input-decimal-value=","
 *   data-currency-input-thousand-value="."
 *   data-currency-input-places-value="2"
 *
 * The input is reformatted with grouping separators on connect and on blur.
 * Right before the surrounding form submits, the visible value is converted
 * back to "1234.56" (plain `.` decimal, no thousand sep) so Symfony's
 * NumberType parses it independently of the request locale.
 */
export default class extends Controller<HTMLInputElement> {

    static values = {
        symbol: String,
        decimal: String,
        thousand: String,
        places: Number,
        allowNegative: Boolean,
    };

    declare readonly symbolValue: string;
    declare readonly decimalValue: string;
    declare readonly thousandValue: string;
    declare readonly placesValue: number;
    declare readonly allowNegativeValue: boolean;

    private boundOnSubmit?: (event: Event) => void;

    connect(): void {
        // Initial format
        this.element.value = this.formatForDisplay(this.element.value);

        this.element.addEventListener('blur', this.onBlur);
        this.element.addEventListener('input', this.onInput);

        const form = this.element.closest('form');
        if (form) {
            this.boundOnSubmit = (): void => {
                this.element.value = this.toRawNumber(this.element.value);
            };
            form.addEventListener('submit', this.boundOnSubmit, { capture: true });
        }
    }

    disconnect(): void {
        this.element.removeEventListener('blur', this.onBlur);
        this.element.removeEventListener('input', this.onInput);
        if (this.boundOnSubmit) {
            const form = this.element.closest('form');
            if (form) form.removeEventListener('submit', this.boundOnSubmit, { capture: true });
        }
    }

    private onBlur = (): void => {
        this.element.value = this.formatForDisplay(this.element.value);
    };

    private onInput = (): void => {
        const allowedSet = new Set<string>([
            ...'0123456789'.split(''),
            this.decimalValue || '.',
            this.thousandValue || ',',
        ]);
        if (this.allowNegativeValue) allowedSet.add('-');

        const filtered = this.element.value
            .split('')
            .filter((ch, idx) => allowedSet.has(ch) || (this.allowNegativeValue && ch === '-' && idx === 0))
            .join('');

        if (filtered !== this.element.value) this.element.value = filtered;
    };

    /**
     * Converts a user-entered value (with sede separators) to a plain
     * decimal string like "1234.56".
     */
    private toRawNumber(value: string): string {
        if (!value) return '';
        const thousand = this.thousandValue || ',';
        const decimal = this.decimalValue || '.';
        let raw = value;
        if (thousand) raw = raw.split(thousand).join('');
        if (decimal !== '.') raw = raw.split(decimal).join('.');
        return raw;
    }

    /**
     * Formats a numeric value (raw or already-formatted) using sede separators
     * and the configured number of decimals.
     */
    private formatForDisplay(value: string): string {
        if (value === '' || value === null || value === undefined) return '';
        const raw = this.toRawNumber(value);
        const num = parseFloat(raw);
        if (Number.isNaN(num)) return '';

        const places = Number.isFinite(this.placesValue) ? this.placesValue : 2;
        const fixed = num.toFixed(places);
        const [intPart, decPart] = fixed.split('.');

        const grouped = (this.thousandValue || '')
            ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandValue)
            : intPart;

        return decPart !== undefined && places > 0
            ? `${grouped}${this.decimalValue || '.'}${decPart}`
            : grouped;
    }
}
